import { defineStore } from 'pinia'
import { ref } from 'vue'
import profilService, { type ProfilComplet, type UserAdresse } from '@/services/profilService'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export const useProfileStore = defineStore('profile', () => {
  const profil = ref<ProfilComplet | null>(null)
  const adresses = ref<UserAdresse[]>([])
  const loading = ref(false)
  let profilFetchedAt = 0
  let adressesFetchedAt = 0
  // Deduplicate concurrent calls
  let profilPromise: Promise<ProfilComplet | null> | null = null
  let adressesPromise: Promise<UserAdresse[]> | null = null

  function isFresh(fetchedAt: number): boolean {
    return fetchedAt > 0 && Date.now() - fetchedAt < CACHE_TTL
  }

  async function fetchProfil(force = false): Promise<ProfilComplet | null> {
    if (!force && isFresh(profilFetchedAt) && profil.value) {
      return profil.value
    }
    if (profilPromise) return profilPromise

    profilPromise = (async () => {
      loading.value = true
      try {
        const res = await profilService.getProfilComplet()
        if (res.data) {
          profil.value = res.data
          profilFetchedAt = Date.now()
        }
        return profil.value
      } finally {
        loading.value = false
        profilPromise = null
      }
    })()

    return profilPromise
  }

  async function fetchAdresses(force = false): Promise<UserAdresse[]> {
    if (!force && isFresh(adressesFetchedAt) && adresses.value.length > 0) {
      return adresses.value
    }
    if (adressesPromise) return adressesPromise

    adressesPromise = (async () => {
      try {
        const res = await profilService.getAdresses()
        if (res.data) {
          adresses.value = Array.isArray(res.data) ? res.data : []
          adressesFetchedAt = Date.now()
        }
        return adresses.value
      } finally {
        adressesPromise = null
      }
    })()

    return adressesPromise
  }

  function invalidateAdresses() {
    adressesFetchedAt = 0
  }

  function $reset() {
    profil.value = null
    adresses.value = []
    profilFetchedAt = 0
    adressesFetchedAt = 0
    profilPromise = null
    adressesPromise = null
    loading.value = false
  }

  return {
    profil,
    adresses,
    loading,
    fetchProfil,
    fetchAdresses,
    invalidateAdresses,
    $reset,
  }
})
